/**
 * BaseNode — Componente base para todos os nodes do canvas.
 *
 * Design A (Rich Preview): faixa colorida da família à esquerda, fundo do card
 * (tokens de tema), handles horizontais (esquerda=in, direita=out), preview do
 * conteúdo, botão "+" à direita. Chrome tematizado via var(--theme-*).
 */

import React, { memo, useEffect, useMemo } from 'react';
import { Handle, Position, useEdges, useUpdateNodeInternals, type NodeProps } from 'reactflow';
import type { AskQuestionActionConfig, WorkflowNodeCancellation } from '@troia-v3/types';
import {
  ASK_QUESTION_ANSWERED_HANDLE,
  ASK_QUESTION_INVALID_HANDLE,
  ASK_QUESTION_NO_REPLY_HANDLE,
  ASK_QUESTION_OPTION_HANDLE_PREFIX,
  askFormOutputHandles,
  askQuestionOutputHandles,
  WORKFLOW_FAILURE_HANDLE,
  WORKFLOW_SUCCESS_HANDLE,
  WORKFLOW_WAIT_CANCELLED_HANDLE,
  nodeTypeHasSuccessFailureOutputs,
  waitHasCancelledOutput,
} from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { CardActionsMenu, Tooltip, type CardAction } from '@/shared/components/ui';
import { useIsMobile } from '@/shared/hooks';
import type { FilterCondition } from '../FilterRow';
import { SKIPPED_REASON_TITLES } from '../../utils/skip-reasons';

// ============================================================================
// Types
// ============================================================================

export interface BaseNodeData {
  label?: string;
  type: string;
  config?: Record<string, unknown>;
  hasError?: boolean;
  disabled?: boolean;
  executionStatus?: {
    status?: string;
    error?: string;
    duration?: number;
    /** Motivo de "Não rodou" (`skipped`) — `output.reason` da run entry: 'filtro_nao_atendido' ou 'execucao_cancelada'. */
    reason?: string;
  };
  previewCount?: number | null;
  onPreviewClick?: () => void;
  /** Callback quando o botão "+" é clicado (abre popup de seleção).
   *  Recebe o nodeId de origem e o handle de saída — o node adicionado herda
   *  a conexão a partir deste node/handle. */
  onPlusClick?: (nodeId: string, sourceHandle?: string) => void;
  /** Aba Filtros do painel de config (Fase 3 liga o motor de filtros). */
  filters?: FilterCondition[];
  /** Aba Cancelamento (todo node, gatilho incluído) — spec 2026-09-14, D1. */
  cancellation?: WorkflowNodeCancellation;
  /** Menu do nó — "Excluir" (spec 2026-09-20 §5.2). A página confirma. */
  onRequestDelete?: (nodeId: string) => void;
  /** Menu do nó — "Visualizar contexto" (spec 2026-09-20 §5.6). */
  onViewRunInputs?: (nodeId: string) => void;
  /**
   * Por que "Visualizar contexto" está desabilitado. Ausente = habilitado.
   * A página preenche enquanto o workflow não tiver sido salvo.
   */
  runInputsDisabledReason?: string;
}

/** Rótulo do item "Visualizar contexto" do menu do nó. */
const VIEW_CONTEXT_LABEL = 'Visualizar contexto';
/**
 * Abaixo de 1024px o menu vira folha inferior, e a folha DESCARTA o motivo do
 * item desabilitado — sem isto o usuário veria a ação cinza e muda. Nessa
 * largura o motivo entra no próprio rótulo.
 */
const VIEW_CONTEXT_LABEL_MOBILE_DISABLED = 'Visualizar contexto (salve o workflow primeiro)';

// Config shape é inferida do registry — sem interface separada

// ============================================================================
// Icon + Color Registry
// ============================================================================

const NODE_REGISTRY: Record<string, { color: string; icon: IconName; label: string }> = {
  trigger_event: { color: '#8b5cf6', icon: 'zap', label: 'Evento' },
  trigger_schedule: { color: '#8b5cf6', icon: 'clock', label: 'Agendamento' },
  trigger_webhook: { color: '#8b5cf6', icon: 'globe', label: 'Webhook' },
  trigger_manual: { color: '#8b5cf6', icon: 'play', label: 'Manual' },
  trigger_date_field: { color: '#8b5cf6', icon: 'calendar', label: 'Data' },
  trigger_inactivity: { color: '#8b5cf6', icon: 'moon', label: 'Inatividade' },
  trigger_instagram_comment: { color: '#8b5cf6', icon: 'camera', label: 'Instagram' },
  trigger_instagram_mention: { color: '#8b5cf6', icon: 'instagram', label: 'Menção IG' },
  action_send_message: { color: '#3b82f6', icon: 'send', label: 'Enviar Mensagem' },
  action_send_email: { color: '#3b82f6', icon: 'mail', label: 'Enviar Email' },
  action_send_template: { color: '#3b82f6', icon: 'file-text', label: 'Template' },
  action_send_media: { color: '#3b82f6', icon: 'image', label: 'Enviar Mídia' },
  action_http_request: { color: '#3b82f6', icon: 'link', label: 'HTTP Request' },
  action_query_database: { color: '#3b82f6', icon: 'database', label: 'Buscar Banco' },
  action_create_lead: { color: '#3b82f6', icon: 'user-plus', label: 'Criar Lead' },
  action_update_lead: { color: '#3b82f6', icon: 'edit', label: 'Atualizar Lead' },
  action_update_contact: { color: '#3b82f6', icon: 'user', label: 'Atualizar Contato' },
  action_add_tag: { color: '#3b82f6', icon: 'tag', label: 'Adicionar Tag' },
  action_remove_tag: { color: '#3b82f6', icon: 'tag', label: 'Remover Tag' },
  action_assign: { color: '#3b82f6', icon: 'users', label: 'Distribuir' },
  action_create_conversation: { color: '#3b82f6', icon: 'message-square', label: 'Criar Conversa' },
  action_transfer_conversation: { color: '#3b82f6', icon: 'corner-up-right', label: 'Transferir Conversa' },
  action_satisfaction_survey: { color: '#3b82f6', icon: 'star', label: 'CSAT' },
  action_ask_question: { color: '#3b82f6', icon: 'help-circle', label: 'Pergunta' },
  action_ask_form: { color: '#3b82f6', icon: 'list-ordered', label: 'Perguntar Formulário' },
  action_next_number: { color: '#3b82f6', icon: 'hash', label: 'Número Sequencial' },
  action_save_form_response: { color: '#3b82f6', icon: 'save', label: 'Salvar Formulário' },
  action_create_ticket: { color: '#3b82f6', icon: 'file-text', label: 'Criar Ticket' },
  action_set_variable: { color: '#3b82f6', icon: 'edit', label: 'Variável' },
  action_internal_notification: { color: '#3b82f6', icon: 'bell', label: 'Notificar' },
  action_find_leads: { color: '#3b82f6', icon: 'search', label: 'Buscar Leads' },
  action_create_checklist: { color: '#3b82f6', icon: 'check-square', label: 'Criar Checklist' },
  action_find_unit: { color: '#3b82f6', icon: 'map-pin', label: 'Buscar Unidade' },
  action_find_user: { color: '#3b82f6', icon: 'user', label: 'Buscar Usuário' },
  action_find_contact: { color: '#3b82f6', icon: 'user-plus', label: 'Buscar Contato' },
  action_url_to_pdf: { color: '#3b82f6', icon: 'file-text', label: 'Gerar PDF' },
  action_nfe_pdf: { color: '#3b82f6', icon: 'file-text', label: 'PDF da Nota' },
  control_if: { color: '#f59e0b', icon: 'git-branch', label: 'Condição' },
  control_switch: { color: '#f59e0b', icon: 'git-branch', label: 'Switch' },
  control_wait_for: { color: '#f59e0b', icon: 'clock', label: 'Aguardar' },
  control_loop: { color: '#f59e0b', icon: 'repeat', label: 'Loop' },
  control_split: { color: '#f59e0b', icon: 'target', label: 'Teste A/B' },
  control_retry_scope: { color: '#f59e0b', icon: 'refresh', label: 'Tentar Novamente' },
  action_voice_clone: { color: '#3b82f6', icon: 'mic', label: 'Clonar Voz' },
  action_voice_tts: { color: '#3b82f6', icon: 'music', label: 'Falar (Clone)' },
  action_voice_clone_delete: { color: '#3b82f6', icon: 'trash', label: 'Deletar Voz' },
  action_mirror_media: { color: '#3b82f6', icon: 'download', label: 'Espelhar Mídia' },
  ai_agent: { color: '#10b981', icon: 'cpu', label: 'Agente IA' },
  ai_agent_inline: { color: '#ef4444', icon: 'zap', label: 'IA Inline' },
  skill_input: { color: '#8b5cf6', icon: 'arrow-right', label: 'Entrada' },
  skill_output: { color: '#8b5cf6', icon: 'log-out', label: 'Saída' },
};

function getNodeMeta(type: string) {
  return NODE_REGISTRY[type] || { color: '#64748b', icon: 'settings' as IconName, label: type };
}

// ============================================================================
// Execution status badge
// ============================================================================

/** O Tooltip do quadro mostra só o começo do erro; o texto inteiro está na sidebar do histórico. */
const TOOLTIP_ERROR_MAX = 300;

const StatusBadge: React.FC<{ nodeId: string; status: string; reason?: string; error?: string }> = ({
  nodeId,
  status,
  reason,
  error,
}) => {
  const config: Record<string, { bg: string; icon: IconName }> = {
    completed: { bg: 'var(--theme-success-color)', icon: 'check' },
    failed: { bg: 'var(--theme-error-color)', icon: 'x' },
    running: { bg: 'var(--theme-info-color)', icon: 'loader' },
    pending: { bg: 'var(--theme-card-muted)', icon: 'clock' },
    suspended: { bg: 'var(--theme-warning-color)', icon: 'pause-circle' },
    // "Não rodou" — filtro da aba Filtros não bateu ou a execução foi
    // cancelada antes de chegar aqui; o motivo (`reason`) vira tooltip.
    skipped: { bg: 'var(--theme-card-muted)', icon: 'minus' },
  };
  const c = config[status];
  if (!c) return null;

  // Dica do selo (Tooltip do sistema, nunca `title=` nativo): o motivo de
  // "Não rodou" ou o erro do nó que falhou (U3 / D20 — o dado já chegava em
  // `executionStatus.error` e nunca era desenhado).
  let hint: string | undefined;
  if (status === 'skipped') {
    hint = SKIPPED_REASON_TITLES[reason ?? ''] ?? 'Não rodou';
  } else if (status === 'failed' && error) {
    hint = error.length > TOOLTIP_ERROR_MAX ? `${error.slice(0, TOOLTIP_ERROR_MAX)}…` : error;
  }

  let testId: string | undefined;
  if (status === 'skipped') testId = 'node-status-badge-skipped';
  else if (status === 'failed') testId = `node-status-error-${nodeId}`;

  const badge = (
    <div
      // `nodrag nopan`: passar o mouse/arrastar sobre o selo não move o nó nem o
      // quadro. Sem stopPropagation — o clique continua chegando ao nó.
      className={`absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center ${hint !== undefined ? 'nodrag nopan' : ''}`}
      style={{ backgroundColor: c.bg, border: '2px solid var(--theme-card-background)', zIndex: 5 }}
      data-testid={testId}
    >
      <Icon name={c.icon} className={`w-2.5 h-2.5 ${status === 'running' ? 'animate-spin' : ''}`} style={{ color: '#fff' }} />
    </div>
  );

  if (hint === undefined) return badge;

  return (
    <Tooltip content={hint} side="top" className="max-w-[320px] whitespace-pre-wrap break-words">
      {badge}
    </Tooltip>
  );
};

// ============================================================================
// Preview renderers
// ============================================================================

function renderConfigPreview(type: string, config: Record<string, unknown>): React.ReactNode {
  // Instagram comment trigger preview
  if (type === 'trigger_instagram_comment') {
    const keyword = typeof config.keyword === 'string' ? config.keyword.trim() : '';
    const postId = typeof config.postId === 'string' ? config.postId.trim() : '';
    return (
      <div className="mt-1 flex flex-col gap-0.5 text-[10px]" style={{ color: 'var(--theme-card-text)' }}>
        <div className="flex items-center gap-1.5">
          <Icon name="search" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
          <span className="font-medium">{keyword ? `Contém "${keyword}"` : 'Qualquer comentário'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Icon name="camera" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
          <span className="font-mono truncate">{postId ? `Post ${postId}` : 'Todos os posts'}</span>
        </div>
      </div>
    );
  }

  // Instagram mention trigger preview
  if (type === 'trigger_instagram_mention') {
    const keyword = typeof config.keyword === 'string' ? config.keyword.trim() : '';
    return (
      <div className="mt-1 flex flex-col gap-0.5 text-[10px]" style={{ color: 'var(--theme-card-text)' }}>
        <div className="flex items-center gap-1.5">
          <Icon name="instagram" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
          <span className="font-medium">{keyword ? `Menção contém "${keyword}"` : 'Qualquer menção'}</span>
        </div>
      </div>
    );
  }

  // Transferir conversa — só o tipo de destino: o nó não busca a lista de
  // usuários/equipes (o nome escolhido aparece no painel de configuração).
  if (type === 'action_transfer_conversation' && (config.transferTo === 'user' || config.transferTo === 'team')) {
    const isTeam = config.transferTo === 'team';
    return (
      <div className="mt-1 flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--theme-card-text)' }} data-testid="node-transfer-preview">
        <Icon name={isTeam ? 'users' : 'user'} className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
        <span className="font-medium">
          {isTeam ? 'Para uma equipe' : 'Para um usuário'}
          {config.targetSource === 'variable' ? ' (variável do fluxo)' : ''}
        </span>
      </div>
    );
  }

  // CSAT — o que o nó faz, em uma linha (a pesquisa em si é a do canal).
  if (type === 'action_satisfaction_survey') {
    const motivos: Record<string, string> = { resolved: 'Resolvido', transferred: 'Transferido', other: 'Outro' };
    const motivo = motivos[String(config.closeReason ?? 'resolved')] ?? 'Resolvido';
    return (
      <div className="mt-1 flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--theme-card-text)' }} data-testid="node-csat-preview">
        <Icon name="star" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
        <span className="font-medium">Encerra ({motivo}) e pede a nota</span>
      </div>
    );
  }

  // Message preview
  if (config.message && typeof config.message === 'string') {
    const msg = config.message as string;
    return (
      <div className="mt-1">
        <div
          className="text-[11px] leading-relaxed rounded-lg px-2.5 py-2 overflow-hidden relative"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', border: '1px solid var(--theme-card-border)', color: 'var(--theme-card-text)', maxHeight: 52 }}
        >
          {msg.substring(0, 80)}{msg.length > 80 ? '...' : ''}
          {msg.length > 60 && <div className="absolute bottom-0 left-0 right-0 h-3" style={{ background: 'linear-gradient(transparent, var(--theme-surface-sunken))' }} />}
        </div>
      </div>
    );
  }

  // Template preview
  if (config.templateId) {
    return (
      <div className="mt-1 flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--theme-card-text)' }}>
        <Icon name="file-text" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
        <span className="font-medium">{String(config.templateId)}</span>
      </div>
    );
  }

  // Tags preview
  if (Array.isArray(config.tags) && config.tags.length > 0) {
    return (
      <div className="mt-1 flex flex-wrap gap-1">
        {(config.tags as string[]).slice(0, 3).map((tag, i) => (
          <span key={i} className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--theme-info-subtle)', color: 'var(--theme-info-fg)' }}>
            {tag}
          </span>
        ))}
        {config.tags.length > 3 && <span className="text-[10px]" style={{ color: 'var(--theme-card-muted)' }}>+{config.tags.length - 3}</span>}
      </div>
    );
  }

  // Condition preview
  if (config.condition && config.operator) {
    return (
      <div className="mt-1 text-[10px] px-2 py-1 rounded" style={{ backgroundColor: 'var(--theme-warning-subtle)', border: '1px solid transparent', color: 'var(--theme-warning-fg)' }}>
        <code className="font-mono text-[9px] px-1 rounded" style={{ backgroundColor: 'var(--theme-card-background)' }}>
          {String(config.condition)}
        </code>
        {' '}{String(config.operator)}{' '}
        {config.value !== undefined && (
          <code className="font-mono text-[9px] px-1 rounded" style={{ backgroundColor: 'var(--theme-card-background)' }}>
            {String(config.value)}
          </code>
        )}
      </div>
    );
  }

  // AI agent preview
  if (config.agentId && type === 'ai_agent') {
    return (
      <div className="mt-1 text-[10px] px-2 py-1 rounded" style={{ backgroundColor: 'var(--theme-success-subtle)', border: '1px solid transparent', color: 'var(--theme-success-fg)' }}>
        Agente configurado
      </div>
    );
  }

  // Wait for preview — node "Aguardar" (control_wait_for). `until` é o
  // único formato suportado (3 modos: duration/time/business_hours).
  if (config.until && type === 'control_wait_for') {
    const until = config.until as { mode?: string; duration?: unknown; unit?: unknown; time?: unknown; businessHours?: unknown };
    let summary = '';
    if (until.mode === 'duration') summary = `${String(until.duration)} ${String(until.unit)}`;
    else if (until.mode === 'time') summary = `até ${String(until.time)}`;
    else if (until.mode === 'business_hours') summary = 'horário comercial';
    return (
      <div className="mt-1 flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--theme-card-text)' }}>
        <Icon name="clock" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
        <span className="font-medium">{summary}</span>
      </div>
    );
  }

  // Event type preview
  if (config.eventType) {
    return (
      <div className="mt-1 flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--theme-card-text)' }}>
        <Icon name="zap" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
        <span className="font-medium">{String(config.eventType)}</span>
      </div>
    );
  }

  // URL preview
  if (config.url) {
    return (
      <div className="mt-1 flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--theme-card-text)' }}>
        <Icon name="link" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
        <span className="font-mono truncate">{String(config.method || 'GET')} {String(config.url).substring(0, 30)}</span>
      </div>
    );
  }

  return null;
}

// ============================================================================
// Outputs config for branching nodes
// ============================================================================

/**
 * Saídas de um nó que ramifica. `null` = uma saída só, sem etiqueta.
 *
 * `cancellation` é a aba Cancelamento do PRÓPRIO nó: só o Aguardar ganha saída
 * por causa dela (spec 2026-09-20 §7.2, D12). Exportada para ser provada sem
 * navegador em `tests/unit/workflow-saida-cancelado.test.ts`.
 */
export function getOutputs(
  type: string,
  config: Record<string, unknown>,
  cancellation?: WorkflowNodeCancellation,
): Array<{ id: string; label: string; color: string; position: number }> | null {
  if (type === 'control_if') {
    return [
      { id: 'true', label: 'Sim', color: '#22c55e', position: 35 },
      { id: 'false', label: 'Não', color: '#ef4444', position: 65 },
    ];
  }
  if (type === 'control_wait_for') {
    // Fonte ÚNICA da pergunta "este Aguardar tem saída Cancelado?" — a mesma
    // que o validador e o compilador usam (spec §7.1). Sem regra própria o nó
    // continua com uma saída só, exatamente como hoje (I9).
    const hasCancelled = waitHasCancelledOutput({
      type: 'control_wait_for',
      data: { label: '', config: {}, ...(cancellation !== undefined ? { cancellation } : {}) },
    });
    if (!hasCancelled) return null;
    return [
      { id: 'output', label: 'Seguiu', color: '#22c55e', position: 35 },
      { id: WORKFLOW_WAIT_CANCELLED_HANDLE, label: 'Cancelado', color: '#ef4444', position: 65 },
    ];
  }
  if (nodeTypeHasSuccessFailureOutputs(type)) {
    // Nó de ação que pode não conseguir (ex.: Transferir conversa). Falha
    // solta = a execução para com erro quando não der — o backend garante.
    return [
      { id: WORKFLOW_SUCCESS_HANDLE, label: 'Sucesso', color: '#22c55e', position: 35 },
      { id: WORKFLOW_FAILURE_HANDLE, label: 'Falha', color: '#ef4444', position: 65 },
    ];
  }
  if (type === 'action_ask_question') {
    // Pergunta (2026-09-28): uma saída por opção (pela marca ESTÁVEL
    // `option-<id>` — nunca o índice) ou "Respondeu", mais "Não entendeu" e
    // "Sem resposta". Fonte ÚNICA das marcas: `askQuestionOutputHandles`,
    // a mesma do validador e do compilador.
    const cfg = config as Partial<AskQuestionActionConfig>;
    const handles = askQuestionOutputHandles(cfg);
    const step = 100 / (handles.length + 1);
    const optionColors = ['#3b82f6', '#10b981', '#8b5cf6', '#0ea5e9', '#14b8a6'];
    return handles.map((id, i) => {
      if (id === ASK_QUESTION_ANSWERED_HANDLE) return { id, label: 'Respondeu', color: '#22c55e', position: step * (i + 1) };
      if (id === ASK_QUESTION_INVALID_HANDLE) return { id, label: 'Não entendeu', color: '#f59e0b', position: step * (i + 1) };
      if (id === ASK_QUESTION_NO_REPLY_HANDLE) return { id, label: 'Sem resposta', color: '#94a3b8', position: step * (i + 1) };
      const optionId = id.slice(ASK_QUESTION_OPTION_HANDLE_PREFIX.length);
      const option = (cfg.options ?? []).find((o) => o.id === optionId);
      const label = option?.label.trim() ? option.label.trim() : `Opção ${i + 1}`;
      return { id, label, color: optionColors[i % optionColors.length] as string, position: step * (i + 1) };
    });
  }
  if (type === 'action_ask_form') {
    // Perguntar formulário (2026-09-28): "Respondeu" e "Não respondeu" —
    // `askFormOutputHandles`, a mesma fonte do validador e do compilador.
    return askFormOutputHandles().map((id, i) =>
      id === ASK_QUESTION_ANSWERED_HANDLE
        ? { id, label: 'Respondeu', color: '#22c55e', position: 35 + i * 30 }
        : { id, label: 'Não respondeu', color: '#94a3b8', position: 35 + i * 30 },
    );
  }
  if (type === 'control_retry_scope') {
    // body = trecho retentado (compiler: handle ≠ 'after'); after = continuação
    return [
      { id: 'body', label: 'Tentar', color: '#f59e0b', position: 35 },
      { id: 'after', label: 'Depois', color: '#22c55e', position: 65 },
    ];
  }
  if (type === 'control_split') {
    const paths = (config.paths as Array<{ label: string }>) || [{ label: 'A' }, { label: 'B' }];
    const step = 100 / (paths.length + 1);
    return paths.map((p, i) => ({
      id: `path-${i}`,
      label: p.label,
      color: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][i % 5],
      position: step * (i + 1),
    }));
  }
  if (type === 'control_switch') {
    const cases = (config.cases as Array<{ label: string }>) || [];
    const hasDefault = config.defaultCase !== false;
    const all = [...cases, ...(hasDefault ? [{ label: 'Padrão' }] : [])];
    if (all.length === 0) return null;
    const step = 100 / (all.length + 1);
    return all.map((c, i) => ({
      id: `case-${i}`,
      label: c.label || `Caso ${i + 1}`,
      color: i === all.length - 1 && hasDefault ? '#94a3b8' : ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'][i % 4],
      position: step * (i + 1),
    }));
  }
  return null;
}

// ============================================================================
// Main Component
// ============================================================================

const BaseNodeComponent: React.FC<NodeProps<BaseNodeData>> = ({ data, selected, id }) => {
  const meta = useMemo(() => getNodeMeta(data.type), [data.type]);
  const isMobile = useIsMobile();
  const config = data.config || {};
  const label = data.label || meta.label;
  const isTrigger = data.type.startsWith('trigger_');
  const isSkillInput = data.type === 'skill_input';
  const isSkillOutput = data.type === 'skill_output';
  const outputs = useMemo(
    () => getOutputs(data.type, config, data.cancellation),
    [data.type, config, data.cancellation],
  );
  const hasMultiOutput = outputs !== null;

  // Assinatura das saídas: muda quando a LISTA muda (a regra do Aguardar entra
  // ou sai, um caso do Switch é acrescentado…).
  const outputsSignature = useMemo(
    () => (outputs === null ? '' : outputs.map((out) => out.id).join('|')),
    [outputs],
  );

  // O React Flow 11 mede as alças na montagem e guarda o resultado: sem este
  // aviso, a saída "Cancelado" nasce sem posição conhecida e a ligação sai do
  // lugar errado até a página ser recarregada. É o mesmo defeito que Switch e
  // Teste A/B já têm ao ganhar/perder um caminho.
  // (No React Flow 11 o hook não recebe nada — quem recebe o id é a função que
  // ele devolve; conferido em node_modules/@reactflow/core/.../useUpdateNodeInternals.d.ts.)
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, outputsSignature, updateNodeInternals]);

  // Check which output handles have edges connected
  const edges = useEdges();
  const connectedOutputs = useMemo(() => {
    const set = new Set<string>();
    for (const edge of edges) {
      if (edge.source === id) {
        set.add(edge.sourceHandle || 'output');
      }
    }
    return set;
  }, [edges, id]);

  // Border style priority: execution > error (selected tratado à parte via cor da borda)
  const getBorderColor = (): string => {
    if (data.executionStatus?.status) {
      const colors: Record<string, string> = {
        completed: 'var(--theme-success-color)',
        failed: 'var(--theme-error-color)',
        running: 'var(--theme-info-color)',
        pending: 'var(--theme-card-border)',
        suspended: 'var(--theme-warning-color)',
      };
      return colors[data.executionStatus.status] || 'transparent';
    }
    if (data.hasError) return 'var(--theme-error-color)';
    return 'transparent';
  };

  const statusOutline = getBorderColor();

  // Menu do nó (•••). Os dois itens existem sempre: esconder "Visualizar
  // contexto" faria "Excluir" virar o item 0 e o separador sumir em silêncio
  // (o divisor do DS só desenha a partir do segundo item).
  const runInputsDisabled =
    typeof data.runInputsDisabledReason === 'string' && data.runInputsDisabledReason.length > 0;
  const menuActions: CardAction[] = [
    {
      label: runInputsDisabled && isMobile ? VIEW_CONTEXT_LABEL_MOBILE_DISABLED : VIEW_CONTEXT_LABEL,
      icon: 'eye',
      testId: `node-menu-view-context-${id}`,
      onClick: () => data.onViewRunInputs?.(id),
      disabled: runInputsDisabled,
      disabledReason: data.runInputsDisabledReason,
    },
    {
      label: 'Excluir',
      icon: 'trash',
      testId: `node-menu-delete-${id}`,
      variant: 'danger',
      dividerBefore: true,
      onClick: () => data.onRequestDelete?.(id),
    },
  ];

  return (
    <div
      className="relative rounded-xl transition-shadow"
      data-testid={`node-${data.type}`}
      /* Nó marcado como culpado por algum erro (local ou do servidor). A cor
         sozinha não dá para afirmar em teste — este atributo dá. */
      data-has-error={data.hasError ? 'true' : 'false'}
      style={{
        width: 260,
        backgroundColor: 'var(--theme-card-background)',
        border: `1px solid ${selected ? 'var(--theme-primary)' : 'var(--theme-card-border)'}`,
        boxShadow: selected
          ? '0 0 0 3px color-mix(in srgb, var(--theme-primary) 22%, transparent), 0 4px 16px rgba(0,0,0,0.10)'
          : '0 2px 10px rgba(0,0,0,0.06)',
        opacity: data.disabled ? 0.4 : 1,
        outline: statusOutline !== 'transparent' ? `2px solid ${statusOutline}` : 'none',
        outlineOffset: 2,
        overflow: 'visible',
      }}
    >
      {/* Left color stripe */}
      <div
        className="absolute left-0 top-0 bottom-0 rounded-l-xl"
        style={{ width: 5, backgroundColor: meta.color }}
      />

      {/* Input handle (left) — hidden for triggers and skill entry node */}
      {!isTrigger && !isSkillInput && (
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          style={{ background: 'var(--theme-card-background)', border: `2px solid ${meta.color}`, width: 11, height: 11, left: -5 }}
        />
      )}

      {/* Tools handle (bottom) — só ai_agent_inline: conecta nós de ação que viram Mastra Tools */}
      {data.type === 'ai_agent_inline' && (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="tools"
            data-testid={`node-tools-handle-${id}`}
            style={{
              background: '#fbbf24',
              border: '2px solid #ef4444',
              width: 11,
              height: 11,
              bottom: -5,
              zIndex: 4,
            }}
          />
          <span
            className="absolute text-[8px] font-semibold"
            style={{ bottom: -16, left: '50%', transform: 'translateX(-50%)', color: '#d97706', whiteSpace: 'nowrap' }}
          >
            Tools
          </span>
        </>
      )}

      {/* Output handle(s) (right) — hidden for skill exit node (terminal) */}
      {!isSkillOutput && (hasMultiOutput ? (
        outputs.map((out) => {
          const isConnected = connectedOutputs.has(out.id);
          return (
            <React.Fragment key={out.id}>
              {/* Handle always present (for drag-to-connect) */}
              <Handle
                type="source"
                position={Position.Right}
                id={out.id}
                style={{
                  background: isConnected ? 'var(--theme-card-background)' : 'transparent',
                  border: isConnected ? `2px solid ${out.color}` : 'none',
                  width: isConnected ? 11 : 22,
                  height: isConnected ? 11 : 22,
                  right: isConnected ? -5 : -11,
                  top: `${out.position}%`,
                  zIndex: 4,
                }}
              />
              {/* "+" button overlay when not connected */}
              {!isConnected && (
                <button
                  type="button"
                  className="absolute flex items-center justify-center rounded-full"
                  data-testid={`node-plus-${id}-${out.id}`}
                  style={{
                    width: 22,
                    height: 22,
                    right: -11,
                    top: `${out.position}%`,
                    transform: 'translateY(-50%)',
                    background: 'var(--theme-primary)',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                    zIndex: 6,
                    boxShadow: '0 2px 8px rgba(139,92,246,0.4)',
                  }}
                  onClick={(e) => { e.stopPropagation(); data.onPlusClick?.(id, out.id); }}
                >
                  +
                </button>
              )}
              {/* Rótulo ancorado pela ESQUERDA: ele nasce depois do nó e cresce
                  para fora. Ancorado pela direita (`right: -36/-40`), o texto
                  crescia para dentro — "Sim"/"Não" cabiam, "Cancelado" passava
                  por cima do "+" e do corpo do nó. Os dois recuos limpam o que
                  está à direita da borda: a alça (até +6px) quando ligado, o
                  "+" (até +11px) quando não. */}
              <span
                className="absolute text-[8px] font-semibold"
                data-testid={`node-output-label-${id}-${out.id}`}
                style={{ left: isConnected ? 'calc(100% + 10px)' : 'calc(100% + 16px)', top: `${out.position}%`, transform: 'translateY(-50%)', color: out.color, whiteSpace: 'nowrap' }}
              >
                {out.label}
              </span>
            </React.Fragment>
          );
        })
      ) : (
        (() => {
          const isConnected = connectedOutputs.has('output');
          return (
            <>
              <Handle
                type="source"
                position={Position.Right}
                id="output"
                style={{
                  background: 'var(--theme-card-background)',
                  border: `2px solid ${meta.color}`,
                  width: 11,
                  height: 11,
                  right: -5,
                  zIndex: 4,
                }}
              />
              {!isConnected && (
                <button
                  type="button"
                  className="absolute flex items-center justify-center rounded-full"
                  data-testid={`node-plus-${id}`}
                  style={{
                    width: 22,
                    height: 22,
                    right: -30,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'var(--theme-primary)',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                    zIndex: 6,
                    boxShadow: '0 2px 8px rgba(139,92,246,0.4)',
                  }}
                  onClick={(e) => { e.stopPropagation(); data.onPlusClick?.(id, 'output'); }}
                >
                  +
                </button>
              )}
            </>
          );
        })()
      ))}

      {/* Execution status badge */}
      {data.executionStatus?.status && (
        <StatusBadge
          nodeId={id}
          status={data.executionStatus.status}
          reason={data.executionStatus.reason}
          error={data.executionStatus.error}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5" style={{ paddingLeft: 14 }}>
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${meta.color}18` }}
        >
          <Icon name={meta.icon} className="w-3.5 h-3.5" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          {/* mockup: rótulo da FAMÍLIA pt-BR em cima (GATILHO / AÇÃO · X / CONTROLE · X) + título bold embaixo */}
          <div className="text-[10px] truncate" style={{ color: 'var(--theme-card-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {(() => {
              const family = data.type.startsWith('trigger') ? 'Gatilho'
                : data.type.startsWith('action') ? 'Ação'
                : data.type.startsWith('ai') ? 'IA'
                : data.type.startsWith('skill') ? 'Skill'
                : 'Controle';
              return meta.label && meta.label.toLowerCase() !== family.toLowerCase()
                ? `${family} · ${meta.label}`
                : family;
            })()}
          </div>
          <div className="text-xs font-semibold truncate" style={{ color: 'var(--theme-text)' }}>{label}</div>
        </div>
        {/* Indicador de horário comercial (envio adiado fora da janela) */}
        {(config?.businessHours as { enabled?: boolean } | undefined)?.enabled === true && (
          <span className="flex-shrink-0" title="Respeita horário comercial" data-testid="node-business-hours-badge">
            <Icon name="clock" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
          </span>
        )}
        {/* Selo de filtros — aba Filtros tem ao menos 1 condição (nós filtráveis, Fase 3). */}
        {(data.filters?.length ?? 0) > 0 && (
          <span className="flex-shrink-0" title="Tem filtro — só roda se casar" data-testid="node-filters-badge">
            <Icon name="filter" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
          </span>
        )}
        {/* Selo de cancelamento — aba Cancelamento tem ao menos 1 regra (todo node, spec 2026-09-14). */}
        {(data.cancellation?.rules.length ?? 0) > 0 && (
          <span className="flex-shrink-0" title="Tem regra de cancelamento" data-testid="node-cancellation-badge">
            <Icon name="x-circle" className="w-3 h-3" style={{ color: 'var(--theme-card-muted)' }} />
          </span>
        )}
        {/* Menu do nó (•••) — "Visualizar contexto" e "Excluir" (spec 2026-09-20).
            `nodrag nopan` NO BOTÃO não é enfeite: com `selectNodesOnDrag` (o
            padrão do React Flow) a seleção do nó acontece no `mousedown` do
            arrasto, onde o `stopPropagation` do React não chega — sem a classe,
            abrir o menu arrasta o nó e ainda o deixa selecionado.
            `triggerTone="theme"` porque o nó acompanha o tema: o hover claro
            fixo pintaria um bloco branco no tema escuro. */}
        <div className="flex-shrink-0">
          <CardActionsMenu
            actions={menuActions}
            size="sm"
            triggerClassName="nodrag nopan"
            triggerTone="theme"
            data-testid={`node-menu-${id}`}
          />
        </div>
      </div>

      {/* Preview content */}
      {config && Object.keys(config).length > 0 && (
        <div className="px-3 pb-2.5" style={{ paddingLeft: 14 }}>
          {renderConfigPreview(data.type, config)}
        </div>
      )}
    </div>
  );
};

export const BaseNode = memo(BaseNodeComponent);
export default BaseNode;
